import React from "react";
import PageHero from "../components/shared/PageHero";
import SectionHeading from "../components/shared/SectionHeading";
import SectionDivider from "../components/decor/SectionDivider";
import CTASection from "../components/shared/CTASection";
import Mandala from "../components/decor/Mandala";
import { IMG, CORE_VALUES, HIGHLIGHTS } from "../data/site";
import { Landmark, Flower2, ScrollText, HandHeart, Music4, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const iconMap = { Landmark, Flower2, ScrollText, HandHeart, Music4, Sparkles };

const timeline = [
  { period: { en: "13th c.", kn: "೧೩ನೇ ಶತಮಾನ" }, title: { en: "Foundation in the Dvaita tradition", kn: "ದ್ವೈತ ಪರಂಪರೆಯ ಸ್ಥಾಪನೆ" }, text: { en: "Sri Sode Vadiraja Matha is established as one of the eight Ashta Mathas of Udupi by Jagadguru Sri Madhvacharya, entrusted with the worship of Sri Krishna and the propagation of Dvaita philosophy.", kn: "ಜಗದ್ಗುರು ಶ್ರೀ ಮಧ್ವಾಚಾರ್ಯರಿಂದ ಉಡುಪಿಯ ಎಂಟು ಅಷ್ಟಮಠಗಳಲ್ಲಿ ಒಂದಾಗಿ ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠವು ಸ್ಥಾಪಿಸಲ್ಪಟ್ಟಿತು. ಶ್ರೀ ಕೃಷ್ಣನ ಪೂಜೆ ಮತ್ತು ದ್ವೈತ ಸಿದ್ಧಾಂತದ ಪ್ರಚಾರವನ್ನು ಇದಕ್ಕೆ ವಹಿಸಲಾಯಿತು." } },
  { period: { en: "13th-15th c.", kn: "೧೩-೧೫ನೇ ಶತಮಾನ" }, title: { en: "The Sarvamoola tradition takes root", kn: "ಸರ್ವಮೂಲ ಪರಂಪರೆಯ ಬೇರೂರಿಸುವಿಕೆ" }, text: { en: "The early Peethadhipatis nurture the study of the Sarvamoola Granthas of Sri Madhva and cultivate the community of Vaishnavas across the Konkan and Karnataka coast.", kn: "ಆದಿ ಪೀಠಾಧಿಪತಿಗಳು ಶ್ರೀ ಮಧ್ವರ ಸರ್ವಮೂಲ ಗ್ರಂಥಗಳ ಅಧ್ಯಯನವನ್ನು ಪೋಷಿಸಿ, ಕೊಂಕಣ ಹಾಗೂ ಕರ್ನಾಟಕ ಕರಾವಳಿಯ ವೈಷ್ಣವ ಸಮುದಾಯವನ್ನು ಬೆಳೆಸಿದರು." } },
  { period: { en: "16th c.", kn: "೧೬ನೇ ಶತಮಾನ" }, title: { en: "Sri Vadiraja Theertha - the transformation", kn: "ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು - ಪರಿವರ್ತನೆ" }, text: { en: "Sri Vadiraja Theertha ascends the Peetha, composing 'Yuktimallika', 'Rukminisha Vijaya', 'Teerthaprabandha' and hundreds of devotional works while pioneering the Paryaya system of Krishna worship at Udupi.", kn: "ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು ಪೀಠಾರೋಹಣ ಮಾಡಿ, 'ಯುಕ್ತಿಮಲ್ಲಿಕಾ', 'ರುಕ್ಮಿಣೀಶ ವಿಜಯ', 'ತೀರ್ಥಪ್ರಬಂಧ' ಮತ್ತು ನೂರಾರು ಭಕ್ತಿ ರಚನೆಗಳನ್ನು ಮಾಡುತ್ತಾ ಉಡುಪಿಯ ಕೃಷ್ಣ ಪೂಜೆಯ ಪರ್ಯಾಯ ವ್ಯವಸ್ಥೆಯನ್ನು ಪ್ರಾರಂಭಿಸಿದರು." } },
  { period: { en: "17th-19th c.", kn: "೧೭-೧೯ನೇ ಶತಮಾನ" }, title: { en: "Expansion of branches & patronage", kn: "ಶಾಖೆಗಳು ಮತ್ತು ಆಶ್ರಯದ ವಿಸ್ತರಣೆ" }, text: { en: "Branch Mathas are established across South and Western India. Royal patronage from Vijayanagara and later dynasties supports temple worship and Vedic scholarship.", kn: "ದಕ್ಷಿಣ ಮತ್ತು ಪಶ್ಚಿಮ ಭಾರತದಾದ್ಯಂತ ಶಾಖಾ ಮಠಗಳು ಸ್ಥಾಪನೆಗೊಂಡವು. ವಿಜಯನಗರ ಹಾಗೂ ನಂತರದ ಸಾಮ್ರಾಜ್ಯಗಳ ರಾಜಾಶ್ರಯವು ದೇವಸ್ಥಾನ ಪೂಜೆ ಮತ್ತು ವೈದಿಕ ವಿದ್ವತ್ತೆಗೆ ಬೆಂಬಲ ನೀಡಿತು." } },
  { period: { en: "20th c.", kn: "೨೦ನೇ ಶತಮಾನ" }, title: { en: "Modern scholarship & publications", kn: "ಆಧುನಿಕ ವಿದ್ವತ್ ಮತ್ತು ಪ್ರಕಟಣೆಗಳು" }, text: { en: "The Matha begins systematic publishing of critical editions, founds Tatvamallika magazine, and expands educational institutions.", kn: "ಮಠವು ವಿಮರ್ಶಾತ್ಮಕ ಆವೃತ್ತಿಗಳ ವ್ಯವಸ್ಥಿತ ಪ್ರಕಟಣೆ ಆರಂಭಿಸಿ, ತತ್ವಮಲ್ಲಿಕಾ ಪತ್ರಿಕೆಯನ್ನು ಸ್ಥಾಪಿಸಿ ಶೈಕ್ಷಣಿಕ ಸಂಸ್ಥೆಗಳನ್ನು ವಿಸ್ತರಿಸಿತು." } },
  { period: { en: "Today", kn: "ಇಂದು" }, title: { en: "Living tradition, global community", kn: "ಜೀವಂತ ಪರಂಪರೆ, ಜಾಗತಿಕ ಸಮುದಾಯ" }, text: { en: "Under the current Peethadhipati, the Matha serves devotees worldwide with daily worship, discourses, publications, festivals, Annadana, and cultural programmes.", kn: "ಪ್ರಸ್ತುತ ಪೀಠಾಧಿಪತಿಗಳ ಮಾರ್ಗದರ್ಶನದಲ್ಲಿ ಮಠವು ಪ್ರಪಂಚದಾದ್ಯಂತದ ಭಕ್ತರಿಗೆ ದೈನಂದಿನ ಪೂಜೆ, ಪ್ರವಚನ, ಪ್ರಕಟಣೆಗಳು, ಉತ್ಸವಗಳು, ಅನ್ನದಾನ ಹಾಗೂ ಸಾಂಸ್ಕೃತಿಕ ಕಾರ್ಯಕ್ರಮಗಳ ಮೂಲಕ ಸೇವೆ ಸಲ್ಲಿಸುತ್ತಿದೆ." } },
];

const ABOUT_COPY = {
  timelineEye: { en: "The Journey", kn: "ಪ್ರವಾಸ" },
  timelineT: { en: "A timeline of the Matha", kn: "ಮಠದ ಇತಿಹಾಸ ಕ್ರಮ" },
  timelineS: { en: "From foundation to the present day - key milestones in the story of Sri Sode Vadiraja Matha.", kn: "ಸ್ಥಾಪನೆಯಿಂದ ಇಂದಿನವರೆಗೆ - ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠದ ಪ್ರಮುಖ ಮೈಲಿಗಲ್ಲುಗಳು." },
  valuesEye: { en: "Six sacred pillars", kn: "ಆರು ಪವಿತ್ರ ಆಧಾರಸ್ತಂಭಗಳು" },
  valuesT: { en: "Values that guide every moment", kn: "ಪ್ರತಿ ಕ್ಷಣವನ್ನೂ ಮಾರ್ಗದರ್ಶಿಸುವ ಮೌಲ್ಯಗಳು" },
  ctaEye: { en: "Continue the journey", kn: "ಪ್ರವಾಸವನ್ನು ಮುಂದುವರೆಸಿ" },
  ctaT: { en: "Walk deeper into the story", kn: "ಕಥೆಯ ಆಳಕ್ಕೆ ಇಳಿಯಿರಿ" },
  ctaS: { en: "Explore the life and teachings of Sri Vadiraja Theertha and the unbroken Guru Parampara of Sode Matha.", kn: "ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಜೀವನ, ಬೋಧನೆಗಳು ಹಾಗೂ ಸೋದೆ ಮಠದ ಅಚ್ಛೇದಿತ ಗುರುಪರಂಪರೆಯನ್ನು ಅನ್ವೇಷಿಸಿ." },
  ctaP: { en: "Sri Vadiraja Theertha", kn: "ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥ" },
  ctaSbtn: { en: "Guru Parampara", kn: "ಗುರುಪರಂಪರೆ" },
};

const ABOUT_DOCUMENT = {
  title: {
    en: "Sode Śrī Vādirāja Maṭha",
    kn: "ಸೋದೆ ಶ್ರೀ ವಾದಿರಾಜ ಮಠ",
  },
  subtitle: {
    en: "Sode, also known as Sonda or Svādi, is one of the most sacred kṣetras of the Mādhva tradition. It is the headquarters of Śrī Sode Vādirāja Maṭha, one of the Aṣṭa Maṭhas of Udupi belonging to the lineage established by Śrī Madhvācārya.",
    kn: "ಸೋದೆ, ಸೊಂಡ ಅಥವಾ ಸ್ವಾದಿ ಎಂದೂ ಕರೆಯಲ್ಪಡುವ, ಮಾಧ್ವ ಸಂಪ್ರದಾಯದ ಅತ್ಯಂತ ಪವಿತ್ರ ಕ್ಷೇತ್ರಗಳಲ್ಲಿ ಒಂದಾಗಿದೆ. ಇದು ಶ್ರೀ ಮಧ್ವಾಚಾರ್ಯರಿಂದ ಸ್ಥಾಪಿತವಾದ ಪರಂಪರೆಗೆ ಸೇರಿದ ಉಡುಪಿಯ ಅಷ್ಟಮಠಗಳಲ್ಲಿ ಒಂದಾದ ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠದ ಪ್ರಧಾನ ಕೇಂದ್ರವಾಗಿದೆ.",
  },
  intro: [
    {
      en: "Śrī Madhvācārya installed Śrī Kṛṣṇa at Udupi and established the tradition of the eight Maṭhas. Among these is the Maṭha whose earlier principal seat was at Kumbhāsi. Śrī Viṣṇu Tīrtha, the brother and disciple of Śrī Madhvācārya, is regarded as the first pontiff of this lineage. The tradition was therefore known as Kumbhāsi Maṭha in its earlier period.",
      kn: "ಶ್ರೀ ಮಧ್ವಾಚಾರ್ಯರು ಉಡುಪಿಯಲ್ಲಿ ಶ್ರೀ ಕೃಷ್ಣನನ್ನು ಪ್ರತಿಷ್ಠಾಪಿಸಿ, ಎಂಟು ಮಠಗಳ ಪರಂಪರೆಯನ್ನು ಸ್ಥಾಪಿಸಿದರು. ಅವುಗಳಲ್ಲಿ, ಹಿಂದಿನ ಕಾಲದಲ್ಲಿ ಕುಂಭಾಸಿಯು ಪ್ರಧಾನ ಪೀಠವಾಗಿದ್ದ ಮಠವೂ ಒಂದಾಗಿದೆ. ಶ್ರೀ ಮಧ್ವಾಚಾರ್ಯರ ಸಹೋದರ ಹಾಗೂ ಶಿಷ್ಯರಾದ ಶ್ರೀ ವಿಷ್ಣು ತೀರ್ಥರು ಈ ಪರಂಪರೆಯ ಪ್ರಥಮ ಪೀಠಾಧಿಪತಿಗಳೆಂದು ಪರಿಗಣಿಸಲ್ಪಡುತ್ತಾರೆ. ಆದ್ದರಿಂದ ಈ ಪರಂಪರೆಯು ಅದರ ಆರಂಭಿಕ ಕಾಲದಲ್ಲಿ ಕುಂಭಾಸಿ ಮಠವೆಂದು ಕರೆಯಲ್ಪಡುತ್ತಿತ್ತು.",
    },
    {
      en: "In this illustrious Guru-paramparā, Śrī Vādirāja Tīrtha appeared as one of its greatest luminaries. During his period, Sode became the principal centre of the Maṭha. Śrī Vādirāja established the Maṭha at Sode and installed the sacred Śrī Rāma Trivikrama deity there. In due course, because Sode became the principal kṣetra of Śrī Vādirāja and because he entered the sacred Vṛndāvana here, the Maṭha came to be known as Sode Maṭha. The official history of the Maṭha records that Śrī Vādirāja constructed the temple at Sonda and installed the Trivikrama deity.",
      kn: "ಈ ಪ್ರಸಿದ್ಧ ಗುರುಪರಂಪರೆಯಲ್ಲಿ ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು ಅದರ ಶ್ರೇಷ್ಠ ಮಹನೀಯರಲ್ಲಿ ಒಬ್ಬರಾಗಿ ಅವತರಿಸಿದರು. ಅವರ ಕಾಲದಲ್ಲಿ ಸೋದೆಯು ಮಠದ ಪ್ರಧಾನ ಕೇಂದ್ರವಾಯಿತು. ಶ್ರೀ ವಾದಿರಾಜರು ಸೋದೆಯಲ್ಲಿ ಮಠವನ್ನು ಸ್ಥಾಪಿಸಿ, ಅಲ್ಲಿ ಪವಿತ್ರ ಶ್ರೀ ರಾಮ ತ್ರಿವಿಕ್ರಮ ದೇವರನ್ನು ಪ್ರತಿಷ್ಠಾಪಿಸಿದರು. ಕಾಲಕ್ರಮೇಣ, ಸೋದೆಯು ಶ್ರೀ ವಾದಿರಾಜರ ಪ್ರಧಾನ ಕ್ಷೇತ್ರವಾಗಿದ್ದುದರಿಂದ ಹಾಗೂ ಅವರು ಇಲ್ಲಿಯೇ ಪವಿತ್ರ ವೃಂದಾವನವನ್ನು ಪ್ರವೇಶಿಸಿದುದರಿಂದ, ಈ ಮಠವು ಸೋದೆ ಮಠವೆಂದು ಪ್ರಸಿದ್ಧವಾಯಿತು. ಮಠದ ಅಧಿಕೃತ ಇತಿಹಾಸದ ಪ್ರಕಾರ, ಶ್ರೀ ವಾದಿರಾಜರು ಸೋಂದೆಯಲ್ಲಿ ದೇವಾಲಯವನ್ನು ನಿರ್ಮಿಸಿ, ತ್ರಿವಿಕ್ರಮ ದೇವರನ್ನು ಪ್ರತಿಷ್ಠಾಪಿಸಿದರು.",
    },
  ],
  sections: [
  {
    title: {
      en: "Śrī Rāma Trivikrama of Sode",
      kn: "ಸೋದೆಯ ಶ್ರೀ ರಾಮ ತ್ರಿವಿಕ್ರಮ",
    },
    paragraphs: [
      {
        en: "The story of Śrī Rāma Trivikrama at Sode is closely connected with Śrī Vādirāja and his devoted servant Śrī Bhūtarāja.",
        kn: "ಸೋದೆಯ ಶ್ರೀ ರಾಮ ತ್ರಿವಿಕ್ರಮ ದೇವರ ಕಥೆಯು ಶ್ರೀ ವಾದಿರಾಜರು ಹಾಗೂ ಅವರ ಭಕ್ತ ಸೇವಕರಾದ ಶ್ರೀ ಭೂತರಾಜರೊಂದಿಗೆ ನಿಕಟವಾಗಿ ಸಂಬಂಧ ಹೊಂದಿದೆ.",
      },
      {
        en: "During his visit to Ūrdhva-Badarikāśrama, Śrī Vādirāja had the darśana of the sacred Trivikrama deity. By divine direction, the deity was brought from Ūrdhva-Badarī to Sode by Bhūtarāja, the devoted servant of Śrī Vādirāja. Śrī Vādirāja subsequently installed the deity at Sode.",
        kn: "ಊರ್ಧ್ವ-ಬದರಿಕಾಶ್ರಮಕ್ಕೆ ಭೇಟಿ ನೀಡಿದ ಸಂದರ್ಭದಲ್ಲಿ ಶ್ರೀ ವಾದಿರಾಜರು ಪವಿತ್ರ ತ್ರಿವಿಕ್ರಮ ದೇವರ ದರ್ಶನ ಪಡೆದರು. ದೈವಿಕ ನಿರ್ದೇಶನದಂತೆ, ಶ್ರೀ ವಾದಿರಾಜರ ಭಕ್ತ ಸೇವಕರಾದ ಭೂತರಾಜರು ಆ ದೇವರನ್ನು ಊರ್ಧ್ವ-ಬದರಿಯಿಂದ ಸೋದೆಗೆ ತಂದರು. ನಂತರ ಶ್ರೀ ವಾದಿರಾಜರು ಆ ದೇವರನ್ನು ಸೋದೆಯಲ್ಲಿ ಪ್ರತಿಷ್ಠಾಪಿಸಿದರು.",
      },
      {
        en: "A remarkable episode is associated with the journey of the deity from Ūrdhva-Badarī to Sode. On the way, a demon (Daitya) obstructed Bhūtarāja. Bhūtarāja destroyed the demon using one of the wheels of the chariot in which the Trivikrama deity was being carried.",
        kn: "ತ್ರಿವಿಕ್ರಮ ದೇವರನ್ನು ಊರ್ಧ್ವ-ಬದರಿಯಿಂದ ಸೋದೆಗೆ ತರುವ ಮಾರ್ಗದಲ್ಲಿ ಒಂದು ಅದ್ಭುತ ಘಟನೆಯು ಸಂಭವಿಸಿತು. ದಾರಿಯಲ್ಲಿ ಒಬ್ಬ ದೈತ್ಯನು ಭೂತರಾಜರನ್ನು ತಡೆದನು. ತ್ರಿವಿಕ್ರಮ ದೇವರನ್ನು ಸಾಗಿಸುತ್ತಿದ್ದ ರಥದ ಒಂದು ಚಕ್ರವನ್ನು ಬಳಸಿ ಭೂತರಾಜರು ಆ ದೈತ್ಯನನ್ನು ಸಂಹರಿಸಿದರು.",
      },
      {
        en: "This is remembered even today in the distinctive form of the ratha at Sode, which has three wheels.",
        kn: "ಸೋದೆಯ ರಥವು ಮೂರು ಚಕ್ರಗಳನ್ನು ಹೊಂದಿರುವ ವಿಶಿಷ್ಟ ರೂಪದಲ್ಲಿ ಇಂದಿಗೂ ಇದನ್ನು ಸ್ಮರಿಸುತ್ತದೆ.",
      },
      {
        en: "Another remarkable feature of the Sode Trivikrama is its theological significance. The Upasane of Śrī Trivikrama in this particular form is especially associated with the R̥ju-devatās in the Śāstras. Thus, the installation and worship of Śrī Rāma Trivikrama by Śrī Vādirāja at Sode holds a special place in the Mādhva tradition.",
        kn: "ಸೋದೆಯ ತ್ರಿವಿಕ್ರಮ ದೇವರ ಮತ್ತೊಂದು ಗಮನಾರ್ಹ ವೈಶಿಷ್ಟ್ಯವೆಂದರೆ ಅದರ ತಾತ್ತ್ವಿಕ ಮಹತ್ವ. ಈ ನಿರ್ದಿಷ್ಟ ರೂಪದಲ್ಲಿರುವ ಶ್ರೀ ತ್ರಿವಿಕ್ರಮನ ಉಪಾಸನೆಯು ಶಾಸ್ತ್ರಗಳಲ್ಲಿ ವಿಶೇಷವಾಗಿ ಋಜು-ದೇವತೆಗಳೊಂದಿಗೆ ಸಂಬಂಧ ಹೊಂದಿದೆ. ಆದ್ದರಿಂದ, ಸೋದೆಯಲ್ಲಿ ಶ್ರೀ ವಾದಿರಾಜರಿಂದ ಶ್ರೀ ರಾಮ ತ್ರಿವಿಕ್ರಮ ದೇವರ ಪ್ರತಿಷ್ಠಾಪನೆ ಮತ್ತು ಆರಾಧನೆಯು ಮಾಧ್ವ ಸಂಪ್ರದಾಯದಲ್ಲಿ ವಿಶೇಷ ಸ್ಥಾನವನ್ನು ಹೊಂದಿದೆ.",
      },
    ],
  },
  {
    title: {
      en: "The Sacred Vṛndāvana of Śrī Vādirāja",
      kn: "ಶ್ರೀ ವಾದಿರಾಜರ ಪವಿತ್ರ ವೃಂದಾವನ",
    },
    paragraphs: [
      {
        en: "Sode is also supremely sacred because Śrī Vādirāja Tīrtha entered the Vṛndāvana alive here. According to the traditional account, on the appointed day he worshipped Śrī Hayagrīva, Śrī Bhuvarāha, Śrī Trivikrama and Śrī Madhvācārya, blessed his successors and devotees, and entered the sacred PanchaVṛndāvana. The official history of the Maṭha records this as his sajīva Vṛndāvana-praveśa.",
        kn: "ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು ಇಲ್ಲಿಯೇ ಜೀವಂತವಾಗಿ ವೃಂದಾವನವನ್ನು ಪ್ರವೇಶಿಸಿದ ಕಾರಣ ಸೋದೆಯು ಅತ್ಯಂತ ಪವಿತ್ರವಾಗಿದೆ. ಸಾಂಪ್ರದಾಯಿಕ ವಿವರಣೆಯ ಪ್ರಕಾರ, ನಿಗದಿತ ದಿನದಂದು ಅವರು ಶ್ರೀ ಹಯಗ್ರೀವ, ಶ್ರೀ ಭುವರಾಹ, ಶ್ರೀ ತ್ರಿವಿಕ್ರಮ ಮತ್ತು ಶ್ರೀ ಮಧ್ವಾಚಾರ್ಯರನ್ನು ಪೂಜಿಸಿ, ತಮ್ಮ ಉತ್ತರಾಧಿಕಾರಿಗಳಿಗೆ ಹಾಗೂ ಭಕ್ತರಿಗೆ ಆಶೀರ್ವಾದ ನೀಡಿ, ಪವಿತ್ರ ಪಂಚವೃಂದಾವನವನ್ನು ಪ್ರವೇಶಿಸಿದರು. ಮಠದ ಅಧಿಕೃತ ಇತಿಹಾಸವು ಇದನ್ನು ಅವರ ಸಜೀವ ವೃಂದಾವನ-ಪ್ರವೇಶವೆಂದು ದಾಖಲಿಸುತ್ತದೆ.",
      },
      {
        en: "The Pañca-Vṛndāvana at Sode is another unique feature of this sacred kṣetra. To the western side of the Vṛndāvana is the sacred Dhavala Gaṅgā, which adds further sanctity to the kṣetra.",
        kn: "ಸೋದೆಯ ಪಂಚ-ವೃಂದಾವನವು ಈ ಪವಿತ್ರ ಕ್ಷೇತ್ರದ ಮತ್ತೊಂದು ವಿಶಿಷ್ಟ ವೈಶಿಷ್ಟ್ಯವಾಗಿದೆ. ವೃಂದಾವನದ ಪಶ್ಚಿಮ ಭಾಗದಲ್ಲಿ ಪವಿತ್ರ ಧವಳ ಗಂಗೆಯಿದ್ದು, ಇದು ಕ್ಷೇತ್ರದ ಪಾವಿತ್ರ್ಯವನ್ನು ಮತ್ತಷ್ಟು ಹೆಚ್ಚಿಸುತ್ತದೆ.",
      },
    ],
  },
  {
    title: {
      en: "The Sacred Deities of Sode",
      kn: "ಸೋದೆಯ ಪವಿತ್ರ ದೇವತೆಗಳು",
    },
    paragraphs: [
      {
        en: "Sode is home to several important temples and deities associated with Śrī Vādirāja's worship.",
        kn: "ಶ್ರೀ ವಾದಿರಾಜರ ಆರಾಧನೆಯೊಂದಿಗೆ ಸಂಬಂಧ ಹೊಂದಿರುವ ಹಲವಾರು ಪ್ರಮುಖ ದೇವಾಲಯಗಳು ಮತ್ತು ದೇವತೆಗಳು ಸೋದೆಯಲ್ಲಿವೆ.",
      },
      {
        en: "Among them are:",
        kn: "ಅವುಗಳಲ್ಲಿ:",
      },
    ],
    items: [
      { en: "Śrī Rāma Trivikrama", kn: "ಶ್ರೀ ರಾಮ ತ್ರಿವಿಕ್ರಮ" },
      { en: "Śrī Mukhya Prāṇa", kn: "ಶ್ರೀ ಮುಖ್ಯ ಪ್ರಾಣ" },
      { en: "Śrī Rudradeva", kn: "ಶ್ರೀ ರುದ್ರದೇವ" },
    ],
  },
  {
    title: {
      en: "Sode - A Sacred Tridhāma",
      kn: "ಸೋದೆ — ಒಂದು ಪವಿತ್ರ ತ್ರಿಧಾಮ",
    },
    paragraphs: [
      {
        en: "Sode is therefore not merely the headquarters of a Maṭha. It is a profound kṣetra of spiritual and historical significance in the Mādhva tradition.",
        kn: "ಆದ್ದರಿಂದ ಸೋದೆಯು ಕೇವಲ ಒಂದು ಮಠದ ಪ್ರಧಾನ ಕೇಂದ್ರವಲ್ಲ. ಇದು ಮಾಧ್ವ ಸಂಪ್ರದಾಯದಲ್ಲಿ ಆಧ್ಯಾತ್ಮಿಕ ಹಾಗೂ ಐತಿಹಾಸಿಕ ಮಹತ್ವವುಳ್ಳ ಮಹತ್ವಪೂರ್ಣ ಕ್ಷೇತ್ರವಾಗಿದೆ.",
      },
      {
        en: "The presence of Śrī Rāma Trivikrama, the sacred Pañca-Vṛndāvana, Dhavala Gaṅgā, the various deities worshipped by Śrī Vādirāja, and the living spiritual legacy of the Guru-paramparā make Sode a uniquely sacred pilgrimage centre.",
        kn: "ಶ್ರೀ ರಾಮ ತ್ರಿವಿಕ್ರಮ, ಪವಿತ್ರ ಪಂಚ-ವೃಂದಾವನ, ಧವಳ ಗಂಗಾ, ಶ್ರೀ ವಾದಿರಾಜರಿಂದ ಆರಾಧಿಸಲ್ಪಟ್ಟ ವಿವಿಧ ದೇವತೆಗಳು ಹಾಗೂ ಗುರುಪರಂಪರೆಯ ಜೀವಂತ ಆಧ್ಯಾತ್ಮಿಕ ಪರಂಪರೆಯ ಸಾನ್ನಿಧ್ಯವು ಸೋದೆಯನ್ನು ವಿಶಿಷ್ಟವಾದ ಪವಿತ್ರ ಯಾತ್ರಾಕ್ಷೇತ್ರವನ್ನಾಗಿ ಮಾಡುತ್ತದೆ.",
      },
      {
        en: "The traditional understanding is that Sode is comparable to the three supreme sacred realms — Śvetadvīpa, Anantaśayana and Vaikuṇṭha. The sanctity of this kṣetra is further enhanced by the presence of the Vṛndāvana of Śrī Vādirāja, who continues to be revered here as the guiding presence of the Maṭha and its devotees.",
        kn: "ಸಾಂಪ್ರದಾಯಿಕ ನಂಬಿಕೆಯ ಪ್ರಕಾರ, ಸೋದೆಯು ಮೂರು ಪರಮ ಪವಿತ್ರ ಲೋಕಗಳಾದ — ಶ್ವೇತದ್ವೀಪ, ಅನಂತಶಯನ ಮತ್ತು ವೈಕುಂಠಗಳಿಗೆ ಸಮಾನವಾಗಿದೆ. ಶ್ರೀ ವಾದಿರಾಜರ ವೃಂದಾವನದ ಸಾನ್ನಿಧ್ಯವು ಈ ಕ್ಷೇತ್ರದ ಪಾವಿತ್ರ್ಯವನ್ನು ಮತ್ತಷ್ಟು ಹೆಚ್ಚಿಸುತ್ತದೆ. ಅವರು ಮಠ ಮತ್ತು ಅದರ ಭಕ್ತರಿಗೆ ಮಾರ್ಗದರ್ಶಕ ಸಾನ್ನಿಧ್ಯವಾಗಿ ಇಲ್ಲಿ ನಿರಂತರವಾಗಿ ಪೂಜಿಸಲ್ಪಡುತ್ತಾರೆ.",
      },
      {
        en: "Thus, Sode stands as a luminous centre of Dvaita Vedānta, Guru-bhakti, Vaiṣṇava worship and the enduring legacy of Śrī Vādirāja Tīrtha.",
        kn: "ಹೀಗೆ, ಸೋದೆಯು ದ್ವೈತ ವೇದಾಂತ, ಗುರುಭಕ್ತಿ, ವೈಷ್ಣವ ಆರಾಧನೆ ಮತ್ತು ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಚಿರಸ್ಥಾಯಿ ಪರಂಪರೆಯ ಪ್ರಕಾಶಮಾನ ಕೇಂದ್ರವಾಗಿ ನಿಂತಿದೆ.",
      },
    ],
  },
  ],
};

const AboutBody = () => {
  const { t, lang } = useLang();
  const L = (o) => (o && o[lang]) || (o && o.en) || "";
  const pick = (o, k) => (lang === "kn" && o[`${k}Kn`]) || o[k];
  return (
    <>
      <PageHero
        eyebrow={t("about.hero.eyebrow")}
        title={L(ABOUT_DOCUMENT.title)}
        subtitle={L(ABOUT_DOCUMENT.subtitle)}
        image={IMG.templeStone}
      />

      {/* Intro two-column */}
      <section className="py-16" data-testid="about-intro">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
              <img src={IMG.templeCorridor} alt="Matha corridor" className="w-full h-full object-cover" data-testid="about-image-1" />
            </div>
            <div className="absolute -top-8 -left-8 opacity-70"><Mandala size={160} color="#D4AF37" opacity={0.6} /></div>
          </div>
          <div>
            <SectionHeading
              eyebrow={lang === "kn" ? "ಪರಂಪರೆ" : "Guru-paramparā"}
              title={L(ABOUT_DOCUMENT.title)}
            />
            <div className="mt-4 space-y-3 text-[#3B2A1A]/85 leading-relaxed">
              {ABOUT_DOCUMENT.intro.map((paragraph) => (
                <p key={L(paragraph).slice(0, 48)} style={{ textAlign: "justify" }}>
                  {L(paragraph)}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sacred kshetra narrative */}
      <section className="bg-sand-texture py-16 relative overflow-hidden" data-testid="about-sacred-kshetra">
        <div className="absolute -top-28 -right-24 opacity-30 animate-mandala-slow">
          <Mandala size={340} color="#B65A14" opacity={0.4} />
        </div>
        <div className="max-w-6xl mx-auto px-4 lg:px-8 relative">
          <div className="mt-12 grid gap-6">
            {ABOUT_DOCUMENT.sections.map((section) => (
              <article
                key={L(section.title)}
                className="rounded-2xl border border-[#D4AF37]/25 bg-[#FBF7F0]/90 p-6 sm:p-8 shadow-sm"
              >
                <h3 className="font-serif-display text-2xl sm:text-3xl text-[#3B2A1A] leading-tight">
                  {L(section.title)}
                </h3>
                <div className="mt-5 space-y-4 text-[#3B2A1A]/85 leading-relaxed">
                  {section.paragraphs.map((paragraph) => (
                    <p key={L(paragraph).slice(0, 48)} style={{ textAlign: "justify" }}>
                      {L(paragraph)}
                    </p>
                  ))}
                </div>
                {section.items && (
                  <ul className="mt-5 grid sm:grid-cols-3 gap-3">
                    {section.items.map((item) => (
                      <li
                        key={L(item)}
                        className="rounded-xl border border-[#D4AF37]/35 bg-[#F2E7D5]/60 px-4 py-3 text-sm font-semibold text-[#3B2A1A]"
                      >
                        {L(item)}
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Additional imagery */}
      <section className="py-24" data-testid="about-gallery">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid md:grid-cols-2 gap-6">
          <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl border border-[#D4AF37]/30">
            <img
              src={require("../assets/9ft0ujmt_sode_kshetra_Enhanced.webp")}
              alt="Sri Sode Vadiraja Matha - view of the main sannidhi"
              className="w-full h-full object-cover"
              data-testid="about-image-2"
            />
          </div>
          <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl border border-[#D4AF37]/30">
            <img
              src={require("../assets/dmte88d4_about_sode.webp")}
              alt="Sri Sode Vadiraja Matha - temple with dhwaja stambha"
              className="w-full h-full object-cover"
              data-testid="about-image-3"
            />
          </div>
        </div>
      </section>

      {/* Timeline */}
      {/* <section className="bg-sand-texture py-16 relative overflow-hidden" data-testid="about-timeline">
        <div className="absolute -bottom-24 -right-24 opacity-40 animate-mandala-slow"><Mandala size={360} color="#B65A14" opacity={0.4} /></div>
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <SectionHeading
            eyebrow={L(ABOUT_COPY.timelineEye)}
            title={L(ABOUT_COPY.timelineT)}
            subtitle={L(ABOUT_COPY.timelineS)}
            align="center"
          />
          <div className="mt-16 relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#D4AF37] to-transparent" />
            <div className="space-y-10">
              {timeline.map((tt, i) => (
                <div key={i} className={`relative grid md:grid-cols-2 gap-6 md:gap-14 ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}>
                  <div className={`pl-12 md:pl-0 ${i % 2 ? "md:text-left" : "md:text-right"}`}>
                    <div className="overline text-[#B65A14] mb-2">{L(tt.period)}</div>
                    <h3 className="font-serif-display text-2xl text-[#3B2A1A]">{L(tt.title)}</h3>
                  </div>
                  <div className={`pl-12 md:pl-0 ${i % 2 ? "" : "md:pl-12"}`}>
                    <p className="text-[#3B2A1A]/80 leading-relaxed">{L(tt.text)}</p>
                  </div>
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 top-1 w-4 h-4 rounded-full bg-[#D4AF37] ring-4 ring-[#F2E7D5]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section> */}

      {/* <SectionDivider variant="lotus" /> */}

      {/* Core Values */}
      {/* <section className="max-w-7xl mx-auto px-4 lg:px-8 py-8" data-testid="about-values">
        <SectionHeading
          eyebrow={L(ABOUT_COPY.valuesEye)}
          title={L(ABOUT_COPY.valuesT)}
          align="center"
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CORE_VALUES.map((v) => {
            const Icon = iconMap[v.icon];
            return (
              <div key={v.title} className="relative bg-[#FBF7F0] border border-[#D4AF37]/25 rounded-2xl p-8 card-lift overflow-hidden" data-testid={`about-value-${v.title.toLowerCase()}`}>
                <div className="absolute -top-8 -right-8 opacity-20"><Mandala size={120} color="#D4AF37" opacity={0.8} /></div>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#B65A14] to-[#3B2A1A] text-[#D4AF37] flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6" />
                </div>
                <div className="overline text-[#B65A14] mb-2">{pick(v, "subtitle")}</div>
                <h3 className="font-serif-display text-2xl text-[#3B2A1A] mb-2">{pick(v, "title")}</h3>
                <p className="text-sm text-[#3B2A1A]/80 leading-relaxed">{pick(v, "desc")}</p>
              </div>
            );
          })}
        </div>
      </section> */}

      {/* Statistics */}
      {/* <section className="py-20" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="bg-[#B65A14] text-[#FBF7F0] rounded-3xl p-10 sm:p-16 relative overflow-hidden">
            <div className="absolute -bottom-20 -right-20 opacity-15 animate-mandala-slow"><Mandala size={340} color="#D4AF37" opacity={1} /></div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-8 text-center relative">
              {HIGHLIGHTS.map((h) => (
                <div key={h.label}>
                  <div className="font-serif-display text-3xl sm:text-4xl text-[#D4AF37]">{pick(h, "value")}</div>
                  <div className="text-xs uppercase tracking-widest text-[#F2E7D5]/85 mt-2 leading-tight">{pick(h, "label")}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section> */}

      <CTASection
        eyebrow={L(ABOUT_COPY.ctaEye)}
        title={L(ABOUT_COPY.ctaT)}
        subtitle={L(ABOUT_COPY.ctaS)}
        primary={{ label: L(ABOUT_COPY.ctaP), to: "/lineage?tab=vadiraja" }}
        secondary={{ label: L(ABOUT_COPY.ctaSbtn), to: "/lineage?tab=guru" }}
        image={IMG.templeInterior}
      />
    </>
  );
};

export const AboutContent = AboutBody;

import PageLayout from "../components/layout/PageLayout";

const About = () => (
  <PageLayout>
    <AboutBody />
  </PageLayout>
);

export default About;
